<template>
    <div :class="baseClass">
        <div :class="`${baseClass}__container`">
            <div
                v-if="mode === 'prompt'"
                class="ask-pendo-input__banner">
                <span class="ask-pendo-input__banner-text">{{ $t('askPendo.Experimental') }}</span>
                <pendo-icon
                    type="beaker"
                    :size="16"
                    stroke="var(--palette-teal-100)" />
            </div>
            <ask-pendo-file-context
                v-if="!!file"
                :file-name="file.name"
                :file-icon="fileIcon"
                removable
                @remove-file="handleFileRemove" />
            <editor-content
                v-if="editor"
                :class="[`${baseClass}__field`, { 'has-file': !!file }]"
                :editor="editor"
                :data-cy="`${baseClass}__field`" />
            <div :class="`${baseClass}__actions`">
                <div
                    v-if="!isGuideChat"
                    :class="`${baseClass}__left-buttons`">
                    <pendo-button
                        v-if="!isCustomerFinder && !isListenAgent"
                        :class="`${baseClass}__mention-button`"
                        type="secondary"
                        icon="mention"
                        :label="$t('askPendo.input.Insert (@)')"
                        @click.stop="handleMentionsButtonClick" />
                    <!-- File upload button and hidden input -->
                    <pendo-button
                        v-if="showFileContext"
                        :class="`${baseClass}__file-upload-button`"
                        type="secondary"
                        size="mini"
                        icon="plus"
                        @click="handleFileUploadClick" />
                    <input
                        ref="fileInput"
                        type="file"
                        accept=".pdf,.docx,.txt,.csv,.md,.xlsx"
                        style="display: none"
                        @change="handleFileChange">

                    <ask-pendo-config-drawer v-if="showConfigDrawer" />
                </div>
                <div
                    :class="`${baseClass}__send-button-wrapper`"
                    :title="disabled && disabledTooltip ? disabledTooltip : undefined">
                    <pendo-button
                        :class="`${baseClass}__send-button`"
                        :type="buttonType"
                        :icon="buttonIcon"
                        :label="buttonLabel"
                        :aria-label="$t('askPendo.input.send message')"
                        size="small"
                        :disabled="isButtonDisabled"
                        :data-cy="`${baseClass}__send-button`"
                        @click="handleAction" />
                </div>
            </div>
        </div>
        <ask-pendo-mention-popover
            v-if="showMentionPopover"
            :mention-data="mentionData"
            :reference-element="popoverReferenceElement"
            :segment-dependents="segmentDependents"
            @mouseenter="onPopoverMouseEnter"
            @mouseleave="onPopoverMouseLeave" />
    </div>
</template>

<script>
import { PendoButton, PendoIcon } from '@pendo/components';
import { Editor, EditorContent } from '@tiptap/vue-3';
import Mention from '@tiptap/extension-mention';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import MentionsPopover from '@/ask-pendo/components/mentions-popover/mentions-popover.vue';
import AskPendoConfigDrawer from '@/ask-pendo/components/ask-pendo-config-drawer.vue';
import AskPendoFileContext from '@/ask-pendo/components/ask-pendo-file-context.vue';
import { renderMention } from '@/common/marked/extension-mentions/render-mention';
import { mapGetters, mapState } from 'vuex';
import { suggestion } from '@/common/tiptap/suggestion';
import AskPendoMentionPopover from '@/ask-pendo/components/ask-pendo-mention-popover.vue';
import mentionPopoverMixin from '@/ask-pendo/utils/mention-popover.mixin.js';
import { getFileIcon } from '@/ask-pendo/utils/file-upload.js';

export default {
    name: 'AskPendoInput',
    components: {
        EditorContent,
        PendoButton,
        PendoIcon,
        AskPendoConfigDrawer,
        AskPendoFileContext,
        AskPendoMentionPopover,
    },
    mixins: [mentionPopoverMixin],
    props: {
        placeholder: {
            type: String,
            default: '',
        },
        loading: {
            type: Boolean,
            default: false,
        },
        typing: {
            type: Boolean,
            default: false,
        },
        mode: {
            type: String,
            default: 'prompt', // 'prompt' or 'conversation'
            validator: (value) => ['prompt', 'conversation'].includes(value),
        },
        customButtonLabel: {
            type: String,
            default: null,
        },
        customButtonType: {
            type: String,
            default: '',
        },
        customButtonIcon: {
            type: String,
            default: 'arrow-right',
        },
        isGuideChat: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        disabledTooltip: {
            type: String,
            default: '',
        },
    },
    emits: ['update', 'stop', 'generate'],
    data () {
        return {
            editor: null,
            query: '',
            isCustomerFinder: false,
            isListenAgent: false,
            // NOTE: Multi-file support in the future. For now only one file is supported.
            files: [],
            isSuggestionOpen: false,
        };
    },
    computed: {
        ...mapGetters({
            features: 'features/list',
            pages: 'pages/list',
            trackEvents: 'trackTypes/list',
            hasSegmentFlag: 'auth/hasSegmentFlag',
            isPendozer: 'auth/isPendozer',
        }),
        ...mapState({
            segmentDependents: (state) => state.segments.segmentDependents,
        }),
        mentionPopoverDisabled () {
            return this.isSuggestionOpen;
        },
        file () {
            // Only one file is supported for now
            return this.files?.length ? this.files[0] : null;
        },
        fileIcon () {
            return getFileIcon(this.file?.type);
        },
        baseClass () {
            return this.mode === 'prompt' ? 'ask-pendo-input' : 'conversation-input';
        },
        inputPlaceholder () {
            if (this.placeholder) {
                return this.placeholder;
            }

            return this.mode === 'prompt'
                ? this.$t('askPendo.input.Ask me anything about your product (enter @ to reference a Pendo item)')
                : this.$t('askPendo.input.Ask another question (enter @ to reference a Pendo item)');
        },
        buttonLabel () {
            if (this.loading || this.typing) {
                return this.$t('askPendo.input.Stop');
            }

            if (this.customButtonLabel) {
                return this.customButtonLabel;
            }

            return null;
        },
        buttonIcon () {
            if (this.loading || this.typing) {
                return 'square';
            }

            return this.customButtonIcon;
        },
        buttonType () {
            if (this.customButtonType) {
                return this.customButtonType;
            }

            return 'primary';
        },
        isButtonDisabled () {
            // Disable if submitting is disabled (e.g., no experimental access)
            if (this.disabled) {
                return true;
            }

            const hasEditorContent = this.editor && !this.editor.isEmpty;

            if (this.mode === 'prompt') {
                return !hasEditorContent;
            }

            // If showing stop button (loading or typing), button should be enabled
            if (this.loading || this.typing) {
                return false;
            }

            // Otherwise, disable if no content
            return !hasEditorContent;
        },
        hasEmployeeConfigAccess () {
            return this.hasSegmentFlag('askPendoConfigDrawer') && this.isPendozer;
        },
        hasCustomInstructionsAccess () {
            return this.hasSegmentFlag('customInstructions');
        },
        showConfigDrawer () {
            return this.hasEmployeeConfigAccess || this.hasCustomInstructionsAccess;
        },
        showFileContext () {
            return this.hasSegmentFlag('agentModeFileUpload');
        },
    },
    async created () {
        this.isCustomerFinder = this.$route.name.includes('CustomerFinder');
        this.isListenAgent = this.$route.name.includes('ListenAgent');
    },
    mounted () {
        const { suggestionConfig, closeSuggestions } = suggestion({
            component: MentionsPopover,
            placement: this.mode === 'prompt' ? 'bottom-start' : 'top-start',
            offset: [-240, 8],
            onOpen: () => { this.isSuggestionOpen = true; },
            onClose: () => { this.isSuggestionOpen = false; },
        });

        this.editor = new Editor({
            extensions: [
                StarterKit.configure({
                    blockquote: false,
                    bulletList: false,
                    codeBlock: false,
                    hardBreak: true, // Enable hardBreak for Shift+Enter new lines
                    heading: false,
                    horizontalRule: false,
                    listItem: false,
                    orderedList: false,
                    bold: false,
                    code: false,
                    italic: false,
                    stroke: false,
                }).extend({
                    addKeyboardShortcuts: () => {
                        return {
                            'Shift-Enter': () => {
                                // Insert a hard break (new line) when Shift+Enter is pressed
                                return this.editor.commands.setHardBreak();
                            },
                            'Enter': () => {
                                // Prevent Enter from submitting new messages while assistant is responding or typing
                                if (this.loading || this.typing) {
                                    return true; // Consume the event but don't submit
                                }
                                this.handleAction();

                                return true;
                            },
                        };
                    },
                }),
                Placeholder.configure({
                    placeholder: this.inputPlaceholder,
                }),
                Mention.configure({
                    HTMLAttributes: {
                        class: 'mention',
                    },
                    deleteTriggerWithBackspace: true,
                    suggestion: {
                        ...suggestionConfig,
                        allow: () => !this.isCustomerFinder && !this.isListenAgent,
                    },
                    renderHTML: ({ node }) => {
                        const { label } = node.attrs;
                        if (!label) return '';

                        try {
                            const meta = JSON.parse(label);

                            return renderMention(meta.type, meta.id, meta.text, false);
                        } catch {
                            return '';
                        }
                    },
                    renderText: ({ node }) => {
                        const { label } = node.attrs;
                        if (!label) return '';

                        return `@{${label}}`;
                    },
                }),
            ],
            onBlur: closeSuggestions,
            onCreate: () => {
                const container = this.getMentionPopoverContainer?.();
                if (container) {
                    container.addEventListener('mouseover', this._mentionPopoverOnMouseEnter);
                    container.addEventListener('mouseout', this._mentionPopoverOnMouseLeave);
                }
            },
        });
    },
    beforeUnmount () {
        this.editor?.destroy();
    },
    methods: {
        // Used by mentionPopoverMixin and editor onCreate
        getMentionPopoverContainer () {
            return this.$el?.querySelector('.tiptap');
        },
        handleFileUploadClick () {
            this.$refs.fileInput.click();
        },
        handleFileChange (event) {
            const file = event.target.files[0];
            if (file) {
                // Only one file is supported for now; keep array shape for future
                this.files = [{ name: file.name, type: file.type }];
                event.target.value = '';
            }
        },
        handleFileRemove () {
            // Only one file is supported for now
            this.files = [];
        },
        handleMentionsButtonClick () {
            if (this.editor) {
                this.editor
                    .chain()
                    .focus()
                    .insertContent('@')
                    .run();
            }
        },
        focus () {
            if (this.editor && this.editor.commands && this.editor.commands.focus) {
                this.editor.commands.focus();
            }
        },
        setTextAndFocus (text) {
            if (this.editor && this.editor.commands) {
                this.editor.commands.setContent(text || '');
                this.editor.commands.focus();
            }
        },
        addSelectedId (type, id) {
            if (!this[type].includes(id)) {
                this[type].push(id);
            }
        },
        removeSelectedId (type, idToRemove) {
            const selectedIds = this[type];
            const updatedArrayOfIds = selectedIds.filter((existingId) => existingId !== idToRemove);
            if (selectedIds.length !== updatedArrayOfIds.length) {
                this[type] = updatedArrayOfIds;
            }
        },
        handleAction () {
            // Block submission if input is disabled
            if (this.disabled) {
                return;
            }

            const inputText = this.editor?.getText() || '';

            if (this.mode === 'prompt') {
                // In prompt mode, always submit if there's content (no stop functionality)
                if (!inputText) return;
                this.$emit('generate', {
                    prompt: inputText,
                    files: this.files?.length ? this.files : [],
                });
                this.files = [];
                this.editor.commands.clearContent();
            } else {
                // In conversation mode, check for stop vs submit
                if (this.loading || this.typing) {
                    // If showing stop button (loading or typing), always stop - never submit text
                    this.$emit('stop');

                    return;
                }

                // Normal submission logic when stop button not enabled
                if (inputText) {
                    this.$emit('update', {
                        text: inputText,
                        files: this.files?.length ? this.files : [],
                    });
                    this.files = [];
                    this.editor.commands.clearContent();
                }
            }
        },
    },
};
</script>

<style scoped lang="less">
.ask-pendo-input,
.conversation-input {
    &__container {
        display: flex;
        flex-direction: column;
        width: 100%;
        background-color: var(--palette-gray-0);
        transition: border-color 0.15s ease-in-out;
        border: 1px solid;

        &:hover {
            border-color: var(--palette-teal-80);
        }
        &:focus-within {
            border-color: var(--palette-teal-70);
        }
    }

    &__field {
        flex: 1;
        min-height: 0;
        width: 100%;
        overflow: hidden;

        :deep(.tiptap) {
            overflow-y: auto;
            resize: none;
            border: none;
            outline: none;
            background: transparent;
            font-family: 'Inter', sans-serif;
            font-size: 16px;
            line-height: 24px;
            color: var(--palette-gray-100);
            box-sizing: border-box;
            width: 100%;
            max-height: 400px;
            display: flex;
            flex-direction: column;

            &:focus {
                outline: none;
            }

            p {
                margin-bottom: 16px;
            }

            // Placeholder text styling
            p.is-editor-empty:first-child::before {
                color: var(--palette-gray-70);
                content: attr(data-placeholder);
                float: left;
                height: 0;
                pointer-events: none;
                font-weight: 400;
                font-size: 16px;
                font-family: Inter;
            }

            .mention {
                padding: 4px 6px;
                border-radius: 4px;
                background-color: var(--palette-gray-10);
                color: var(--palette-gray-100);
                font-size: 16px;
                font-weight: 600;
                line-height: 24px;
                cursor: default;
                transition: background-color 0.2s ease;
                white-space: normal;
                box-decoration-break: clone;
                svg {
                    stroke: var(--palette-gray-100);
                    fill: none;
                    stroke-linecap: round;
                    stroke-linejoin: round;
                    transform: translateY(1px);
                    margin-right: 4px;
                }
                &[data-type='app'],
                &[data-type='segment'],
                &[data-type='visitor'] svg {
                    stroke-width: 2;
                }
                &[data-type='app'] svg {
                    transform: translateY(2px);
                }
            }

            // Custom scrollbar styling
            &::-webkit-scrollbar {
                width: 15px;
                background: transparent;
            }

            &::-webkit-scrollbar-track {
                background-color: transparent;
            }

            &::-webkit-scrollbar-thumb {
                background-color: var(--palette-gray-40);
                cursor: default;
            }
        }
    }

    &__actions {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    &__file-upload-button,
    &__mention-button {
        min-width: 32px;
        height: 26px;
        padding: 0;
    }

    &__left-buttons {
        display: flex;
        flex-direction: row;
        align-items: center;

        .ask-pendo-config-drawer {
            margin-left: 8px;
        }
    }

    &__send-button {
        align-self: flex-end;
        border-radius: 8px;
        :deep(span) {
            font-weight: 500;
        }
    }
}

.ask-pendo-input {
    width: 100%;
    margin: 60px auto 0 auto;

    :deep(.tiptap) {
        padding: 24px 24px 0 20px;
    }

    &__container {
        border-radius: 8px;
        border-color: var(--palette-gray-30);
        position: relative;
    }

    &__banner {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 16px;
        background-color: var(--palette-teal-5);
        border-radius: 8px 8px 0 0;
        border-bottom: 1px solid var(--palette-gray-30);
    }

    &__banner-text {
        font-size: 16px;
        font-weight: 600;
        color: var(--palette-teal-100);
    }

    &__actions {
        padding: 0 16px 16px 16px;
    }

    &__footer {
        width: 100%;
        text-align: center;
        color: var(--palette-gray-70);
        font-size: 12.642px;
        padding: 8px 0;
    }
}

.conversation-input {
    background-color: transparent;
    flex-shrink: 0;
    width: 100%;

    &__container {
        max-width: 100%;
        border-radius: 8px;
        border-color: var(--palette-gray-40);
        position: relative;
    }

    &__field {
        height: auto;
        max-height: 358px;
        min-height: 45px;

        :deep(.tiptap) {
            padding: 16px 16px 0 16px;
        }
    }

    &__send-button {
        margin-left: auto;
    }

    &__actions {
        padding: 2px 16px 16px 16px;
        align-items: flex-end;
    }
}
</style>
